import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import * as TaskManager from 'expo-task-manager';
import * as BackgroundFetch from 'expo-background-fetch';
import {Platform} from 'react-native';
import {serializeLastOrderResponse} from './orderHistorySlim';

// Define task name for processing the offline queue
export const PROCESS_OFFLINE_QUEUE_TASK = 'PROCESS_OFFLINE_QUEUE_TASK';

// Keys for AsyncStorage
const OFFLINE_ORDERS_QUEUE_KEY = 'offline_orders_queue';
const PROCESSING_QUEUE_KEY = 'processing_offline_queue';

/**
 * Add an order to the offline queue
 * @param {Object} orderData - The order data to be queued
 * @returns {Promise<boolean>} - Whether the order was successfully added to the queue
 */
export const addToOfflineQueue = async (orderData) => {
  try {
    // Generate a unique ID for this queue item
    const queueItemId = `order_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    // Create a queue item with metadata
    const queueItem = {
      id: queueItemId,
      orderData,
      timestamp: Date.now(),
      attempts: 0,
      status: 'pending'
    };

    // Get the current queue
    const queueString = await AsyncStorage.getItem(OFFLINE_ORDERS_QUEUE_KEY);
    const queue = queueString ? JSON.parse(queueString) : [];

    // Add the new item to the queue
    queue.push(queueItem);

    // Save the updated queue
    await AsyncStorage.setItem(OFFLINE_ORDERS_QUEUE_KEY, JSON.stringify(queue));

    console.log(`Order added to offline queue with ID: ${queueItemId}`);
    return true;
  } catch (error) {
    console.error('Failed to add order to offline queue:', error);
    return false;
  }
};

/**
 * Get the current offline queue
 * @returns {Promise<Array>} - The current offline queue
 */
export const getOfflineQueue = async () => {
  try {
    const queueString = await AsyncStorage.getItem(OFFLINE_ORDERS_QUEUE_KEY);
    return queueString ? JSON.parse(queueString) : [];
  } catch (error) {
    console.error('Failed to get offline queue:', error);
    return [];
  }
};

/**
 * Update the status of a queue item
 * @param {string} queueItemId - The ID of the queue item to update
 * @param {string} status - The new status ('pending', 'processing', 'completed', 'failed')
 * @param {Object} result - Optional result data
 * @returns {Promise<boolean>} - Whether the update was successful
 */
export const updateQueueItemStatus = async (queueItemId, status, result = null) => {
  try {
    const queueString = await AsyncStorage.getItem(OFFLINE_ORDERS_QUEUE_KEY);
    if (!queueString) return false;

    const queue = JSON.parse(queueString);
    const itemIndex = queue.findIndex(item => item.id === queueItemId);

    if (itemIndex === -1) return false;

    queue[itemIndex].status = status;
    queue[itemIndex].lastUpdated = Date.now();

    if (status === 'processing') {
      queue[itemIndex].attempts += 1;
    }

    if (result) {
      queue[itemIndex].result = result;
    }

    await AsyncStorage.setItem(OFFLINE_ORDERS_QUEUE_KEY, JSON.stringify(queue));
    return true;
  } catch (error) {
    console.error(`Failed to update queue item ${queueItemId} status:`, error);
    return false;
  }
};

/**
 * Remove a completed item from the queue
 * @param {string} queueItemId - The ID of the queue item to remove
 * @returns {Promise<boolean>} - Whether the removal was successful
 */
export const removeFromQueue = async (queueItemId) => {
  try {
    const queueString = await AsyncStorage.getItem(OFFLINE_ORDERS_QUEUE_KEY);
    if (!queueString) return false;

    const queue = JSON.parse(queueString);
    const updatedQueue = queue.filter(item => item.id !== queueItemId);

    await AsyncStorage.setItem(OFFLINE_ORDERS_QUEUE_KEY, JSON.stringify(updatedQueue));
    return true;
  } catch (error) {
    console.error(`Failed to remove queue item ${queueItemId}:`, error);
    return false;
  }
};

/**
 * Check if there are any pending items in the queue
 * @returns {Promise<boolean>} - Whether there are pending items
 */
export const hasPendingItems = async () => {
  try {
    const queue = await getOfflineQueue();
    return queue.some(item => item.status === 'pending');
  } catch (error) {
    console.error('Failed to check for pending items:', error);
    return false;
  }
};

/**
 * Process the next pending item in the queue
 * @returns {Promise<Object>} - The result of processing the item
 */
export const processNextQueueItem = async () => {
  try {
    // Check if we're already processing the queue
    const isProcessing = await AsyncStorage.getItem(PROCESSING_QUEUE_KEY);
    if (isProcessing === 'true') {
      console.log('Queue is already being processed');
      return { success: false, reason: 'already_processing' };
    }

    // Set the processing flag
    await AsyncStorage.setItem(PROCESSING_QUEUE_KEY, 'true');

    // Get the current queue
    const queue = await getOfflineQueue();

    // Find the next pending item
    const nextItem = queue.find(item => item.status === 'pending');

    if (!nextItem) {
      console.log('No pending items in the queue');
      await AsyncStorage.setItem(PROCESSING_QUEUE_KEY, 'false');
      return { success: false, reason: 'no_pending_items' };
    }

    // Check for network connectivity
    const netInfo = await NetInfo.fetch();
    if (!netInfo.isConnected) {
      console.log('No network connection, cannot process queue');
      await AsyncStorage.setItem(PROCESSING_QUEUE_KEY, 'false');
      return { success: false, reason: 'no_network' };
    }

    console.log(`Processing queue item: ${nextItem.id}`);

    // Update the item status to processing
    await updateQueueItemStatus(nextItem.id, 'processing');

    try {
      // Check the type of queue item
      if (nextItem.orderData.type === 'order_update') {
        // This is an order update, handle it differently
        try {
          // Import the necessary function to process the order update
          const { triggerStoreOrder } = require('./BackgroundTasks');

          // Process the order update
          const result = await triggerStoreOrder(nextItem.orderData.orderData);

          if (result) {
            // Order update was successfully processed
            console.log(`Order update queue item ${nextItem.id} processed successfully`);

            // Update the local stored order
            const updatedOrder = {
              ...nextItem.orderData.orderData,
              status: "closed" // Update status to closed since it's now synced
            };

            // Store the updated order in AsyncStorage
            await AsyncStorage.setItem('lastOrderResponse', serializeLastOrderResponse(updatedOrder));

            await updateQueueItemStatus(nextItem.id, 'completed', { success: true });
            await removeFromQueue(nextItem.id);
            await AsyncStorage.setItem(PROCESSING_QUEUE_KEY, 'false');
            return { success: true, itemId: nextItem.id };
          } else {
            // Order update processing failed
            console.error(`Failed to process order update queue item ${nextItem.id}`);
            await updateQueueItemStatus(nextItem.id, 'failed', { success: false });
            await AsyncStorage.setItem(PROCESSING_QUEUE_KEY, 'false');
            return { success: false, reason: 'processing_failed', itemId: nextItem.id };
          }
        } catch (error) {
          console.error(`Error processing order update queue item ${nextItem.id}:`, error);
          await updateQueueItemStatus(nextItem.id, 'failed', { success: false, error: error.message });
          await AsyncStorage.setItem(PROCESSING_QUEUE_KEY, 'false');
          return { success: false, reason: 'error', error: error.message, itemId: nextItem.id };
        }
      } else {
        // This is a regular order, process it normally
        const { triggerStoreOrder } = require('./BackgroundTasks');

        // Process the order
        const result = await triggerStoreOrder(nextItem.orderData);

        if (result) {
          // Order was successfully processed
          console.log(`Queue item ${nextItem.id} processed successfully`);
          await updateQueueItemStatus(nextItem.id, 'completed', { success: true });
          await removeFromQueue(nextItem.id);
          await AsyncStorage.setItem(PROCESSING_QUEUE_KEY, 'false');
          return { success: true, itemId: nextItem.id };
        } else {
          // Order processing failed
          console.error(`Failed to process queue item ${nextItem.id}`);
          await updateQueueItemStatus(nextItem.id, 'failed', { success: false });
          await AsyncStorage.setItem(PROCESSING_QUEUE_KEY, 'false');
          return { success: false, reason: 'processing_failed', itemId: nextItem.id };
        }
      }
    } catch (error) {
      console.error(`Error processing queue item ${nextItem.id}:`, error);
      await updateQueueItemStatus(nextItem.id, 'failed', { success: false, error: error.message });
      await AsyncStorage.setItem(PROCESSING_QUEUE_KEY, 'false');
      return { success: false, reason: 'error', error: error.message, itemId: nextItem.id };
    }
  } catch (error) {
    console.error('Failed to process next queue item:', error);
    await AsyncStorage.setItem(PROCESSING_QUEUE_KEY, 'false');
    return { success: false, reason: 'error', error: error.message };
  }
};

/**
 * Process all pending items in the queue
 * @returns {Promise<Object>} - The result of processing the queue
 */
export const processQueue = async () => {
  try {
    const results = [];
    const MAX_ITERATIONS = 100; // Safety limit to prevent infinite loops
    let iterations = 0;

    while (iterations < MAX_ITERATIONS) {
      iterations++;
      const result = await processNextQueueItem();
      results.push(result);

      // Stop on any non-success or when there are no more items
      if (!result.success) {
        break;
      }
    }

    if (iterations >= MAX_ITERATIONS) {
      console.warn('[OfflineQueue] processQueue hit safety limit – stopping');
    }

    return { success: true, results };
  } catch (error) {
    console.error('Failed to process queue:', error);
    return { success: false, error: error.message };
  }
};

// Define the background task for processing the offline queue
TaskManager.defineTask(PROCESS_OFFLINE_QUEUE_TASK, async () => {
  try {
    const result = await processQueue();

    if (result.success) {
      return BackgroundFetch.BackgroundFetchResult.NewData;
    } else if (result.reason === 'no_pending_items') {
      return BackgroundFetch.BackgroundFetchResult.NoData;
    } else {
      return BackgroundFetch.BackgroundFetchResult.Failed;
    }
  } catch (error) {
    console.error('Background queue processing task error:', error);
    return BackgroundFetch.BackgroundFetchResult.Failed;
  }
});

/**
 * Register the background task for processing the offline queue
 * @returns {Promise<boolean>} - Whether the registration was successful
 */
export const registerQueueProcessingTask = async () => {
  // Background tasks are unavailable on web (PWA/browser); skip silently.
  if (Platform.OS === 'web') return false;

  try {
    await BackgroundFetch.registerTaskAsync(PROCESS_OFFLINE_QUEUE_TASK, {
      minimumInterval: 15 * 60, // 15 minutes
      stopOnTerminate: false,
      startOnBoot: true,
    });

    console.log('Offline queue processing task registered successfully');
    return true;
  } catch (error) {
    console.error('Failed to register offline queue processing task:', error);
    return false;
  }
};

/**
 * Manually trigger the queue processing task
 * @returns {Promise<Object>} - The result of processing the queue
 */
export const triggerQueueProcessing = async () => {
  try {
    // Check for network connectivity
    const netInfo = await NetInfo.fetch();
    if (!netInfo.isConnected) {
      console.log('No network connection, cannot process queue');
      return { success: false, reason: 'no_network' };
    }

    // Process the queue
    return await processQueue();
  } catch (error) {
    console.error('Failed to trigger queue processing:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check if the device is online
 * @returns {Promise<boolean>} - Whether the device is online
 */
export const isOnline = async () => {
  try {
    // For web platform, use navigator.onLine
    if (Platform.OS === 'web') {
      return navigator.onLine;
    }

    // For other platforms, use NetInfo
    const netInfo = await NetInfo.fetch();
    return netInfo.isConnected;
  } catch (error) {
    console.error('Failed to check online status:', error);
    return false;
  }
};
